/**
 * EVC Batch Transaction Hook
 *
 * This hook provides the full Euler transaction execution flow:
 * - Simulation with debug tracing
 * - State overrides for permits
 * - Oracle price updates
 * - Proper transaction factory conversion
 *
 * Uses wagmi-evc's useSimulateEVCBatchCall which handles:
 * - Converting TransactionItems to EVC batch calls via GeneralTransactionFactoryUnifier
 * - Oracle price updates (Pyth, Redstone)
 * - State overrides for permits
 * - Gas estimation
 * - Debug trace calls for error analysis
 */

import { useCallback } from 'react'
import { useAccount, useConfig, useWriteContract } from 'wagmi'
import { type Address, type Hex } from 'viem'
import {
  useSimulateEVCBatchCall,
  useSimulation,
  type TransactionItems,
  type DepositTransactionItem,
  type BorrowTransactionItem,
  type SkimTransactionItem,
  type WrapTransactionItem,
  type UnwrapTransactionItem,
  type WithdrawTransactionItem,
  type RepayTransactionItem,
  TransactionLabel,
  type LeverageTransactionItem,
  type TransferTransactionItem,
  type Asset,
} from '@eulerxyz/wagmi-evc'
import { contractBook } from '@eulerxyz/core-sdk'
import {
  getSubAccountAddress,
  getSubAccountId as getSubAccountIdShared,
} from '@/domain/euler/shared/subAccounts'

// =============================================================================
// Types
// =============================================================================

export interface EvcBatchTransactionParams {
  /** The transactions to execute */
  transactions: TransactionItems
  /** Whether to enable the transaction (for conditional execution) */
  enabled?: boolean
}

export interface EvcBatchTransactionResult {
  /** Simulation data including request object */
  simulation: ReturnType<typeof useSimulateEVCBatchCall>
  /** Full simulation with state tracking */
  fullSimulation: ReturnType<typeof useSimulation>
  /** Execute the batch transaction */
  execute: () => Promise<void>
  /** Whether the transaction is being written */
  isWriting: boolean
  /** Write error if any */
  writeError: Error | null
  /** Transaction hash after successful write */
  txHash: Hex | undefined
  /** Reset the write state */
  reset: () => void
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * Hook for executing EVC batch transactions using Euler's full stack.
 *
 * Build TransactionItems directly (DepositTransactionItem, BorrowTransactionItem, etc.)
 * and pass them to this hook. The wagmi-evc simulation pipeline handles validation,
 * factory conversion, oracle updates, and gas estimation.
 */
export function useEvcBatchTransaction({
  transactions,
  enabled = true,
}: EvcBatchTransactionParams): EvcBatchTransactionResult {
  const { address } = useAccount()
  const config = useConfig()

  // Simulation hooks from wagmi-evc
  const simulation = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: enabled && !!transactions?.length && !!address,
      retry: false,
    },
  })

  const fullSimulation = useSimulation({
    config,
    transactions,
    query: {
      enabled: enabled && !!transactions?.length && !!address,
      retry: false,
    },
  })

  // Write contract hook
  const { writeContract, isPending: isWriting, error: writeError, data: txHash, reset } = useWriteContract()

  // Execute the batch transaction
  const execute = useCallback(async () => {
    if (!simulation.data?.request) {
      throw new Error('Simulation not ready')
    }

    writeContract({
      ...simulation.data.request,
      abi: contractBook.evc.abi,
      functionName: 'batch',
    } as any)
  }, [simulation.data, writeContract])

  return {
    simulation,
    fullSimulation,
    execute,
    isWriting,
    writeError,
    txHash,
    reset,
  }
}

// =============================================================================
// Sub-Account Utilities (delegates to shared implementation)
// =============================================================================

/**
 * Get the sub-account address for a given primary address and sub-account ID.
 * Delegates to the canonical implementation in domain/euler/shared/subAccounts.
 */
export function getSubAccount(primary: Address, subAccountId: number): Address {
  return getSubAccountAddress(primary, subAccountId)
}

/**
 * Get the sub-account ID from a primary and sub-account address pair.
 * Returns 0 if the pair is invalid (backward-compatible behavior).
 */
export function getSubAccountId(primary: Address, subAccount: Address): number {
  return getSubAccountIdShared(primary, subAccount) ?? 0
}

// Re-export useful types
export type {
  TransactionItems,
  DepositTransactionItem,
  BorrowTransactionItem,
  SkimTransactionItem,
  WrapTransactionItem,
  UnwrapTransactionItem,
  WithdrawTransactionItem,
  RepayTransactionItem,
  LeverageTransactionItem,
  TransferTransactionItem,
  Asset,
}

export { TransactionLabel }
